import { useState, type ReactNode } from "react";
import Button from "../../../shared/components/Button";
import { Box } from "../../../shared/components/Box";
import { Container } from "../../../shared/components/Container";
import { Grid } from "../../../shared/components/Grid";
import { Layout } from "../../../layouts/Layout";
import { Dropdown } from "../../../shared/components/form";
import "../pages/EventsPage.scss";

type EventKind = "Motion" | "Person" | "System";

type EventItem = {
  kind: EventKind;
  title: string;
  camera: string;
  time: string;
  detail: string;
  confidence?: string;
};

const events: EventItem[] = [
  {
    kind: "Person",
    title: "Person detected",
    camera: "CAM-01 · North entrance",
    time: "08:42:16",
    detail: "Movement across entrance zone",
    confidence: "94%",
  },
  {
    kind: "Motion",
    title: "Motion detected",
    camera: "CAM-01 · North entrance",
    time: "08:41:58",
    detail: "Activity continued for 12 seconds",
    confidence: "88%",
  },
  {
    kind: "System",
    title: "Camera stream recovered",
    camera: "CAM-01 · North entrance",
    time: "08:37:04",
    detail: "RTSP connection restored",
  },
  {
    kind: "Motion",
    title: "Motion detected",
    camera: "CAM-01 · North entrance",
    time: "08:19:31",
    detail: "Movement near the east boundary",
    confidence: "76%",
  },
  {
    kind: "Person",
    title: "Person detected",
    camera: "CAM-01 · North entrance",
    time: "07:56:09",
    detail: "Subject entered monitored area",
    confidence: "91%",
  },
];

const kindStyles: Record<EventKind, string> = {
  Motion: "motion",
  Person: "person",
  System: "system",
};

function Icon({
  children,
  className = "icon",
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <svg
      aria-hidden="true"
      className={`icon ${className}`}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
    >
      {children}
    </svg>
  );
}

function BellIcon() {
  return (
    <Icon>
      <path d="M10.268 21a2 2 0 0 0 3.464 0" />
      <path d="M3.262 15.326A1 1 0 0 0 4 17h16a1 1 0 0 0 .74-1.673C19.41 13.956 18 12.499 18 8a6 6 0 0 0-12 0c0 4.499-1.41 5.956-2.738 7.326" />
    </Icon>
  );
}

function EventIcon({ kind }: { kind: EventKind }) {
  if (kind === "Person")
    return (
      <Icon className="small">
        <circle cx="12" cy="8" r="3" />
        <path strokeLinecap="round" d="M6.5 19a5.5 5.5 0 0 1 11 0" />
      </Icon>
    );
  if (kind === "System")
    return (
      <Icon className="small">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M12 3v3m0 12v3M3 12h3m12 0h3m-3.4-6.6-2.1 2.1m-7 7-2.1 2.1m0-11.2 2.1 2.1m7 7 2.1 2.1"
        />
        <circle cx="12" cy="12" r="3" />
      </Icon>
    );
  return (
    <Icon className="small">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M4 13h4l2-7 3 12 2-5h5"
      />
    </Icon>
  );
}

export function EventsDesktopView() {
  const [cameraFilter, setCameraFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState<"all" | EventKind>("all");
  const visibleEvents = events.filter(
    (event) =>
      (cameraFilter === "all" || event.camera.startsWith(cameraFilter)) &&
      (typeFilter === "all" || event.kind === typeFilter),
  );
  return (
    <Layout activePath="/events" className="events-page">
      <Box as="main" className="main">
        <header className="header">
          <div>
            <p className="eyebrow">Saturday, 08 August 2026</p>
            <h1 className="title">Events</h1>
          </div>
          <Button aria-label="Notifications" className="notifications">
            <BellIcon />
            <span />
          </Button>
        </header>

        <Container className="content" fluid>
          <Grid as="section" className="summary">
            <div>
              <p>Events today</p>
              <strong>128</strong>
            </div>
            <div>
              <p>Motion events</p>
              <strong className="warning">42</strong>
            </div>
            <div>
              <p>Unreviewed</p>
              <strong>7</strong>
            </div>
          </Grid>

          <section>
            <div className="timeline-heading">
              <div>
                <h2>Event timeline</h2>
                <p>Recent activity across monitored locations</p>
              </div>
              <div className="filters">
                <Dropdown
                  aria-label="Filter events by camera"
                  value={cameraFilter}
                  onChange={(event) => setCameraFilter(event.target.value)}
                >
                  <option value="all">All cameras</option>
                  <option value="CAM-01">CAM-01 · North entrance</option>
                </Dropdown>
                <Dropdown
                  aria-label="Filter events by type"
                  value={typeFilter}
                  onChange={(event) =>
                    setTypeFilter(event.target.value as "all" | EventKind)
                  }
                >
                  <option value="all">All types</option>
                  <option value="Motion">Motion</option>
                  <option value="Person">Person</option>
                  <option value="System">System</option>
                </Dropdown>
              </div>
            </div>
            <div className="event-list">
              {visibleEvents.length === 0 ? (
                <p className="empty">No events match filters.</p>
              ) : (
                visibleEvents.map((event, index) => (
                  <article
                    className={`event-row ${index > 0 ? "separated" : ""}`}
                    key={`${event.time}-${event.title}`}
                  >
                    <div className={`event-symbol ${kindStyles[event.kind]}`}>
                      <EventIcon kind={event.kind} />
                    </div>
                    <div className="event-copy">
                      <div>
                        <h3>{event.title}</h3>
                        <span>{event.kind}</span>
                      </div>
                      <p>
                        {event.camera} · {event.detail}
                      </p>
                    </div>
                    <div className="event-time">
                      <p>{event.time}</p>
                      {event.confidence && (
                        <small>{event.confidence} confidence</small>
                      )}
                    </div>
                  </article>
                ))
              )}
            </div>
          </section>
        </Container>
      </Box>
    </Layout>
  );
}
